import { computed } from 'vue'
import { usePermissionsStore } from '@/store/usePermissionsStore'

/**
 * Composable for checking user permissions in components
 * Provides convenient helpers for permission-based UI rendering
 */
export const usePermissions = () => {
  const store = usePermissionsStore()

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission string (e.g., 'prompts:read')
   * @returns {boolean}
   */
  const can = (permission) => {
    return store.hasPermission(permission)
  }

  /**
   * Check if user has any of the specified permissions (OR logic)
   * @param {...string} permissions - Permission strings
   * @returns {boolean}
   */
  const canAny = (...permissions) => {
    return store.hasAnyPermission(permissions)
  }

  /**
   * Check if user has all of the specified permissions (AND logic)
   * @param {...string} permissions - Permission strings
   * @returns {boolean}
   */
  const canAll = (...permissions) => {
    return store.hasAllPermissions(permissions)
  }

  /**
   * Check if user has a specific role
   * @param {string} roleName - Role name to check
   * @returns {boolean}
   */
  const hasRole = (roleName) => {
    return store.hasRole(roleName)
  }

  /**
   * Check if user has any of the specified roles
   * @param {...string} roleNames - Role names
   * @returns {boolean}
   */
  const hasAnyRole = (...roleNames) => {
    return store.hasAnyRole(roleNames)
  }

  // Reactive computed properties for template use
  const permissions = computed(() => store.permissions)
  const roles = computed(() => store.roles)
  const isLoading = computed(() => store.isLoading)
  const isLoaded = computed(() => store.isLoaded)
  const error = computed(() => store.error)

  return {
    // Permission check methods
    can,
    canAny,
    canAll,
    // Role check methods
    hasRole,
    hasAnyRole,
    // Reactive state
    permissions,
    roles,
    isLoading,
    isLoaded,
    error,
    // Store actions
    loadPermissions: store.loadPermissions.bind(store),
    clearPermissions: store.clearPermissions.bind(store)
  }
}
